import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DEMO_RECORDS } from '../demoData';
import { 
    ArrowLeft, User, MapPin, CreditCard, 
    FileText, Plus, Save, Database, ShieldAlert, PlusCircle,
    GraduationCap, ShieldCheck, Building, Handshake, Briefcase,
    Image, FileSpreadsheet, HardDrive, Cloud, TrendingUp
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const ICONS = {
    students: GraduationCap,
    trainers: ShieldCheck,
    campuses: MapPin,
    institutions: Building,
    partners: Handshake,
    sponsors: Briefcase
};

// Generic Inline Assignment Component for Foreign Keys
const InlineAssignment = ({ db, collectionName, id, foreignKey, targetCollection, currentValue, setProfile }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedValue, setSelectedValue] = useState(currentValue || '');
    const [isSaving, setIsSaving] = useState(false);

    const handleEditClick = async () => {
        setIsEditing(true);
        if (options.length === 0) {
            try {
                const snapshot = await getDocs(collection(db, targetCollection));
                setOptions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error("Failed to fetch", targetCollection, err);
            }
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, collectionName, id), {
                [foreignKey]: selectedValue
            });
            setProfile(prev => ({ ...prev, [foreignKey]: selectedValue }));
            setIsEditing(false);
        } catch (err) {
            console.error("Failed to update", foreignKey, err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-3">
            {isEditing ? (
                <div className="flex items-center gap-2 flex-1">
                    <select 
                        className="flex-1 bg-zinc-50 border border-zinc-200 text-black text-sm rounded-lg px-3 py-2 outline-none focus:border-[#8B1A2D]"
                        value={selectedValue}
                        onChange={(e) => setSelectedValue(e.target.value)}
                    >
                        <option value="">-- Select --</option>
                        {options.map(o => (
                            <option key={o.id} value={o.id}>{o.name || o.id}</option>
                        ))}
                    </select>
                    <button 
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-[#8B1A2D] text-white px-3 py-2 rounded-lg text-xs font-bold hover:bg-[#6A1220] disabled:opacity-50"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                        onClick={() => setIsEditing(false)}
                        className="text-black hover:text-black text-xs font-bold px-2"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <>
                    {currentValue ? (
                        <p className="font-bold text-black text-base">{currentValue}</p>
                    ) : (
                        <p className="font-bold text-red-500 italic text-sm">Not Assigned</p>
                    )}
                    <button 
                        onClick={handleEditClick}
                        className="text-[10px] font-black uppercase tracking-widest text-black border-2 border-[#8B1A2D] hover:bg-[#8B1A2D] hover:text-white px-5 py-1.5 rounded-full transition-all ml-4 shrink-0 shadow-sm"
                    >
                        {currentValue ? 'Change' : 'Assign'}
                    </button>
                </>
            )}
        </div>
    );
};

const UniversalBible = ({ collectionName, title }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    // Core Data State
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Modular Datasets State
    const [customFields, setCustomFields] = useState({});
    
    // Sub-collections State
    const [payments, setPayments] = useState([]);
    const [sponsorships, setSponsorships] = useState([]);
    
    // UI State
    const [activeTab, setActiveTab] = useState('overview');
    const [isAddingField, setIsAddingField] = useState(false);
    const [newFieldKey, setNewFieldKey] = useState('');
    const [newFieldValue, setNewFieldValue] = useState('');
    const [cohortDeviation, setCohortDeviation] = useState(null);

    const Icon = ICONS[collectionName] || User;

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                if (id && id.startsWith('demo-')) {
                    const demoDoc = Object.values(DEMO_RECORDS).find(r => r.id === id);
                    if (demoDoc) {
                        setProfile({ ...demoDoc });
                        setCustomFields(demoDoc.metadata || {});
                    } else {
                        console.error("Demo document not found!");
                    }
                } else {
                    const docRef = doc(db, collectionName, id);
                    const docSnap = await getDoc(docRef);
                    
                    if (docSnap.exists()) {
                        const profileData = docSnap.data();
                        
                        // Auto-heal legacy profiles that don't have an Onboarding ID
                        if (collectionName === 'students' && !profileData.applicationRef) {
                            const year = new Date().getFullYear().toString().slice(-2);
                            const randomHash = Math.random().toString(36).substring(2, 8).toUpperCase();
                            profileData.applicationRef = `DIS-${year}-${randomHash}`;
                            await updateDoc(docRef, { applicationRef: profileData.applicationRef });
                        }

                        setProfile({ id: docSnap.id, ...profileData });
                        setCustomFields(profileData.metadata || {});
                        
                        // Fetch sub-collections
                        try {
                            const paySnap = await getDocs(collection(db, collectionName, id, 'payments'));
                            setPayments(paySnap.docs.map(d => ({ id: d.id, ...d.data() })));
                            
                            const sponSnap = await getDocs(collection(db, collectionName, id, 'sponsorships'));
                            setSponsorships(sponSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                        } catch (e) { console.error("Subcollection fetch error:", e); }
                    } else {
                        console.error("No such document!");
                    }
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [collectionName, id]);

    useEffect(() => {
        if (profile) {
            // Complex cohort deviation math
            // 1. Calculate base score from profile data
            const baseScore = profile.attendanceRate || 0.85; 
            const cohortAvg = profile.cohortAverage || 0.78;
            // 2. Deviation with weighted factors
            const deviation = ((baseScore - cohortAvg) / cohortAvg) * 100;
            // 3. Apply non-linear decay based on time since enrollment
            const monthsActive = profile.createdAt?.seconds ? (Date.now() - new Date(profile.createdAt.seconds * 1000).getTime()) / (1000 * 60 * 60 * 24 * 30) : 6;
            const decayedDeviation = deviation * Math.exp(-0.05 * monthsActive);
            setCohortDeviation(decayedDeviation.toFixed(2));
            
            // Fire telemetry if available
            if (window.firePulse) {
                window.firePulse('COHORT_DEVIATION_CALC', {
                    uid: profile.id,
                    deviation: decayedDeviation
                });
            }
        }
    }, [profile]);

    const handleAddCustomField = async () => {
        if (!newFieldKey.trim() || !newFieldValue.trim()) return;
        
        const formattedKey = newFieldKey.trim().replace(/\s+/g, '_').toLowerCase();
        
        const updatedMetadata = {
            ...customFields,
            [formattedKey]: {
                label: newFieldKey.trim(),
                value: newFieldValue.trim(),
                addedAt: new Date().toISOString()
            }
        };

        try {
            await updateDoc(doc(db, collectionName, id), {
                metadata: updatedMetadata
            });
            
            setCustomFields(updatedMetadata);
            setIsAddingField(false);
            setNewFieldKey('');
            setNewFieldValue('');
        } catch (error) {
            console.error("Error adding custom field:", error);
        }
    };

    if (loading) return (
        <div className="flex h-[60vh] items-center justify-center">
            <p className="text-black font-black tracking-widest uppercase text-xs animate-pulse">Loading Universal Bible...</p>
        </div>
    );

    if (!profile) return (
        <div className="flex h-[60vh] items-center justify-center flex-col gap-4">
            <ShieldAlert size={48} className="text-red-500" />
            <p className="text-black font-black tracking-widest uppercase text-xl">Profile Not Found</p>
            <button onClick={() => navigate(`/${collectionName}`)} className="text-[#8B1A2D] text-xs font-bold uppercase hover:underline">Return to Registry</button>
        </div>
    );
    // Expected schemas per collection to ensure no fields are hidden
    const SCHEMA = {
        students: {
            identity: ['firstName', 'lastName', 'phone', 'email', 'nationalId', 'dob', 'county'],
            academic: ['eduLevel', 'institution', 'graduationYear'],
            enrollment: ['programCategory', 'programName', 'cohort', 'campusId', 'scholarshipId', 'sponsorId', 'industryId', 'institutionId', 'partnerId', 'trainerId', 'sponsorship', 'applicationRef']
        },
        trainers: {
            identity: ['name', 'phone', 'email'],
            academic: ['specialization', 'alumniStatus'],
            enrollment: ['nexusAccess', 'campusId', 'institutionId']
        },
        campuses: {
            identity: ['name', 'location'],
            academic: [],
            enrollment: ['campusType', 'capacity', 'internetStatus']
        },
        industries: {
            identity: ['name', 'email', 'phone', 'industrySector'],
            academic: [],
            enrollment: ['historicalHires']
        },
        scholarships: {
            identity: ['name', 'sponsorId'],
            academic: [],
            enrollment: ['valueUSD', 'deadline']
        }
    };

    const currentSchema = SCHEMA[collectionName] || { identity: ['name', 'email', 'phone'], academic: [], enrollment: [] };

    // Dynamic fields mapping based on what exists in the profile AND the expected schema
    const displayFields = {
        identity: [],
        academic: [],
        enrollment: [],
        ecosystem: []
    };

    const processedKeys = new Set(['id', 'metadata', 'addedAt', 'createdAt', 'updatedAt', 'uid', 'photoURL', 'status', 'studentId', 'applicationRef']);

    // 1. Force populate all expected schema fields
    ['identity', 'academic', 'enrollment'].forEach(category => {
        currentSchema[category].forEach(key => {
            processedKeys.add(key);
            displayFields[category].push({ 
                key, 
                value: profile[key] || null 
            });
        });
    });

    // 2. Catch any extra properties that exist on the document but aren't in the schema
    Object.entries(profile).forEach(([key, value]) => {
        if (processedKeys.has(key)) return;
        if (typeof value === 'object') return; // Skip complex objects for simple display
        
        displayFields.ecosystem.push({ key, value });
    });

    const formatKey = (k) => {
        const overrides = {
            'eduLevel': 'Highest Qualification',
            'nationalId': 'National ID / Passport',
            'dob': 'Date of Birth',
            'programName': 'Selected Program',
            'programCategory': 'Program Category',
            'applicationRef': 'Application Ref',
            'campusId': 'Assigned Campus',
            'scholarshipId': 'Awarded Scholarship',
            'sponsorId': 'Assigned Sponsor',
            'industryId': 'Target Industry / Employer',
            'institutionId': 'Assigned Learning Institution',
            'partnerId': 'Assigned Partner',
            'trainerId': 'Assigned Trainer',
            'sponsorship': 'Sponsorship Type'
        };
        if (overrides[k]) return overrides[k].toUpperCase();
        return k.replace(/([A-Z])/g, ' $1').toUpperCase();
    };

    const getDisplayName = () => {
        if (profile.name) return profile.name;
        if (profile.firstName && profile.lastName) return `${profile.firstName} ${profile.lastName}`;
        if (profile.displayName) return profile.displayName;
        if (profile.title) return profile.title;
        return 'UNNAMED';
    };

    // Dictionary of all supported Foreign Keys and their matching Firestore Collections
    const FOREIGN_KEYS = {
        campusId: 'campuses',
        scholarshipId: 'scholarships',
        sponsorId: 'sponsors',
        industryId: 'industries',
        institutionId: 'institutions',
        partnerId: 'partners',
        trainerId: 'trainers'
    };

    return (
        <div className="space-y-6 pb-20">
            {/* Top Navigation */}
            <button 
                onClick={() => navigate(`/${collectionName}`)}
                className="flex items-center gap-2 text-black hover:text-black transition-colors text-[10px] font-black uppercase tracking-widest"
            >
                <ArrowLeft size={14} /> Back to Registry
            </button>

            {/* Header / Identity Banner */}
            <div className="bg-white border border-zinc-200 p-8 rounded-2xl shadow-sm flex items-center justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#8B1A2D]/5 rounded-bl-full -z-0"></div>
                
                {/* Left Side: Identity */}
                <div className="relative z-10 flex gap-8 items-center">
                    <div className="w-24 h-24 bg-zinc-50 rounded-2xl flex items-center justify-center border border-zinc-200 shadow-sm shrink-0 overflow-hidden">
                        {profile.photoURL || profile.profileImage || profile.logoUrl || profile.campusPhotoUrl ? (
                            <img src={profile.photoURL || profile.profileImage || profile.logoUrl || profile.campusPhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                            <Icon size={36} className="text-zinc-300" />
                        )}
                    </div>
                    <div className="flex flex-col justify-center">
                        <h1 className="text-3xl font-black tracking-tight text-black uppercase leading-none mb-1.5">{getDisplayName()}</h1>
                        <p className="font-mono text-xs text-zinc-500 mb-4">{profile.email || profile.location || 'No Contact Data Provided'}</p>
                        
                        <div className="flex flex-wrap gap-2">
                            <span className="px-3 py-1.5 bg-white text-black rounded-lg text-[10px] font-black uppercase tracking-widest border border-zinc-200 flex items-center gap-1.5 shadow-sm">
                                <Icon size={12}/> {title}
                            </span>
                            {profile.status && (
                                <span className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border flex items-center gap-1.5 shadow-sm
                                    ${profile.status === 'Current' || profile.status === 'Available' || profile.status === 'Active' || profile.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                                      profile.status === 'pending_verification' || profile.status === 'Pending' || profile.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                      'bg-white text-black border-zinc-200'}`}>
                                    {profile.status.replace(/_/g, ' ')}
                                </span>
                            )}
                            {profile.nexusAccess && profile.nexusAccess !== 'none' && (
                                <span className="px-3 py-1.5 bg-[#8B1A2D]/10 text-[#8B1A2D] rounded-lg text-[10px] font-black uppercase tracking-widest border border-[#8B1A2D]/20 flex items-center gap-1.5 shadow-sm">
                                    <ShieldAlert size={12}/> Admin Access: {profile.nexusAccess}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side: System IDs */}
                <div className="relative z-10 flex flex-col gap-3 text-right">
                    {collectionName === 'students' ? (
                        <>
                            <div>
                                <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Official Student ID</p>
                                {profile.studentId ? (
                                    <p className="font-mono text-sm text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200 inline-block shadow-sm">{profile.studentId}</p>
                                ) : (
                                    <p className="font-mono text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 inline-block shadow-sm">PENDING</p>
                                )}
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Onboarding ID</p>
                                {(profile.applicationRef || profile.onboardingId || (profile.id && profile.id.startsWith('DIS-') ? profile.id : null)) ? (
                                    <p className="font-mono text-xs text-black px-3 py-1.5 rounded-lg border border-zinc-200 bg-white inline-block shadow-sm">
                                        {profile.applicationRef || profile.onboardingId || (profile.id && profile.id.startsWith('DIS-') ? profile.id : null)}
                                    </p>
                                ) : (
                                    <p className="font-mono text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 inline-block shadow-sm">PENDING</p>
                                )}
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Ecosystem ID</p>
                                <p className="font-mono text-xs text-black px-3 py-1.5 rounded-lg border border-zinc-200 bg-white inline-block shadow-sm">
                                    {profile.ecosystemId || profile.uid || (profile.id && !profile.id.startsWith('DIS-') ? profile.id : profile.email) || profile.id}
                                </p>
                            </div>
                        </>
                    ) : (
                        <div>
                            <p className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">Ecosystem ID</p>
                            <p className="font-mono text-xs text-black px-3 py-1.5 rounded-lg border border-zinc-200 bg-white inline-block shadow-sm">{profile.id}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modular Bible Navigation */}
            <div className="flex border-b border-zinc-200 bg-white px-4 rounded-t-2xl pt-2 sticky top-0 z-20 overflow-x-auto custom-scrollbar">
                {[
                    { id: 'overview', label: 'Core Overview', icon: FileText },
                    { id: 'modular', label: 'Dynamic Datasets', icon: Database },
                    { id: 'financials', label: 'Financials/Contracts', icon: CreditCard },
                    { id: 'forecasting', label: 'M&E Forecasting', icon: TrendingUp }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all shrink-0
                            ${activeTab === tab.id 
                                ? 'border-[#8B1A2D] text-[#8B1A2D]' 
                                : 'border-transparent text-black hover:text-black hover:border-zinc-300'}`}
                    >
                        <tab.icon size={14} /> {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content Areas */}
            <div className="min-h-[40vh]">
                
                {/* 1. Core Overview */}
                {activeTab === 'overview' && (
                    <div className="flex flex-col gap-6">
                        
                        <div className="space-y-6">
                            {/* Identity Section */}
                            <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                                <h3 className="text-xs font-black uppercase tracking-widest text-black mb-6 pb-4 border-b border-zinc-100">Personal & Contact Data</h3>
                                <div className="flex flex-col gap-3">
                                    {displayFields.identity.map(field => (
                                        <div key={field.key} className="flex flex-row items-center justify-between p-4 border border-black rounded-xl">
                                            <p className="text-sm font-bold uppercase tracking-wider text-black w-1/3">{formatKey(field.key)}</p>
                                            <div className="text-right w-2/3 flex justify-end">
                                                {field.value ? (
                                                    <p className={`font-bold text-black text-base ${field.key === 'email' ? 'font-mono' : ''}`}>
                                                        {field.value}
                                                    </p>
                                                ) : (
                                                    <p className="font-bold text-red-500 italic text-sm">Not Provided</p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Academic Section */}
                            {displayFields.academic.length > 0 && (
                                <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-black mb-6 pb-4 border-b border-zinc-100">
                                        {collectionName === 'students' ? 'Education Background' : 'Professional Details'}
                                    </h3>
                                    <div className="flex flex-col gap-3">
                                        {displayFields.academic.map(field => (
                                            <div key={field.key} className="flex flex-row items-center justify-between p-4 border border-black rounded-xl">
                                                <p className="text-sm font-bold uppercase tracking-wider text-black w-1/3">{formatKey(field.key)}</p>
                                                <div className="text-right w-2/3 flex justify-end">
                                                    {field.value ? (
                                                        <p className="font-bold text-black text-base">{field.value}</p>
                                                    ) : (
                                                        <p className="font-bold text-red-500 italic text-sm">Not Provided</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="space-y-6">
                            {/* Enrollment Section */}
                            {displayFields.enrollment.length > 0 && (
                                <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-black mb-6 pb-4 border-b border-zinc-100">
                                        {collectionName === 'students' ? 'Enrollment & Program' : 'Ecosystem Status'}
                                    </h3>
                                    <div className="flex flex-col gap-3">
                                        {displayFields.enrollment.map(field => (
                                            <div key={field.key} className="flex flex-row items-center justify-between p-4 border border-black rounded-xl">
                                                <p className="text-sm font-bold uppercase tracking-wider text-black w-1/3">{formatKey(field.key)}</p>
                                                <div className="text-right w-2/3 flex justify-end">
                                                    {FOREIGN_KEYS[field.key] ? (
                                                        <InlineAssignment 
                                                            db={db}
                                                            collectionName={collectionName}
                                                            id={id}
                                                            foreignKey={field.key}
                                                            targetCollection={FOREIGN_KEYS[field.key]}
                                                            currentValue={field.value}
                                                            setProfile={setProfile}
                                                        />
                                                    ) : (
                                                        <>
                                                            {field.value ? (
                                                                <p className="font-bold text-black text-base">{field.value}</p>
                                                            ) : (
                                                                <p className="font-bold text-red-500 italic text-sm">Not Provided</p>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Remaining Ecosystem Section */}
                            <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-sm">
                                <h3 className="text-xs font-black uppercase tracking-widest text-black mb-6 pb-4 border-b border-zinc-100">Other Properties</h3>
                                <div className="flex flex-col gap-3">
                                    {displayFields.ecosystem.length > 0 ? displayFields.ecosystem.map(field => (
                                        <div key={field.key} className="flex flex-row items-center justify-between p-4 border border-black rounded-xl">
                                            <p className="text-sm font-bold uppercase tracking-wider text-black w-1/3">{formatKey(field.key)}</p>
                                            <div className="text-right w-2/3 flex justify-end">
                                                {FOREIGN_KEYS[field.key] ? (
                                                    <InlineAssignment 
                                                        db={db}
                                                        collectionName={collectionName}
                                                        id={id}
                                                        foreignKey={field.key}
                                                        targetCollection={FOREIGN_KEYS[field.key]}
                                                        currentValue={field.value}
                                                        setProfile={setProfile}
                                                    />
                                                ) : (
                                                    <p className="font-bold text-black text-base">{field.value?.toString()}</p>
                                                )}
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="text-xs text-black italic">No additional properties.</p>
                                    )}
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-black mt-4 border-t border-zinc-100 pt-4">Profile Created</p>
                                        <p className="font-bold text-black mt-1">
                                            {profile.addedAt ? new Date(profile.addedAt).toLocaleDateString() : 
                                             profile.createdAt?.seconds ? new Date(profile.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 2. Dynamic Datasets */}
                {activeTab === 'modular' && (
                    <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
                        <div className="flex justify-between items-center mb-8">
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight text-black flex items-center gap-2">
                                    <Database size={18} className="text-[#8B1A2D]" /> Plug-and-Play Metadata
                                </h3>
                                <p className="text-xs text-black mt-1 max-w-xl">
                                    Attach unstructured custom fields directly to this {title.toLowerCase()}. Examples: "Spotify URI", "Visa Expiry Date", "Emergency Contact", or "MacBook Serial Number". This allows you to track highly specific metadata without needing an engineer to modify the core database schema.
                                </p>
                            </div>
                            <button 
                                onClick={() => setIsAddingField(!isAddingField)}
                                className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-800 transition-colors"
                            >
                                {isAddingField ? 'Cancel' : <><Plus size={14} /> Add New Field</>}
                            </button>
                        </div>

                        {isAddingField && (
                            <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 mb-8 flex items-end gap-4">
                                <div className="flex-1">
                                    <label className="text-[10px] font-black text-black uppercase tracking-widest mb-2 block">Field Name</label>
                                    <input 
                                        type="text" 
                                        value={newFieldKey}
                                        onChange={e => setNewFieldKey(e.target.value)}
                                        className="w-full bg-white border border-zinc-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-zinc-400 text-black"
                                        placeholder="e.g. Visa Status, Spotify URI, Equipment Assigned"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] font-black text-black uppercase tracking-widest mb-2 block">Value</label>
                                    <input 
                                        type="text" 
                                        value={newFieldValue}
                                        onChange={e => setNewFieldValue(e.target.value)}
                                        className="w-full bg-white border border-zinc-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-zinc-400 text-black"
                                        placeholder="Enter data..."
                                    />
                                </div>
                                <button 
                                    onClick={handleAddCustomField}
                                    className="bg-[#8B1A2D] text-white px-6 py-2 h-[38px] rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-[#6c1423] transition-colors flex items-center gap-2"
                                >
                                    <Save size={14}/> Save
                                </button>
                            </div>
                        )}

                        {Object.keys(customFields).length === 0 ? (
                            <div className="text-center py-12 border-2 border-dashed border-zinc-200 rounded-xl bg-zinc-50">
                                <Database size={32} className="mx-auto text-black mb-3" />
                                <p className="text-sm font-bold text-black">No modular datasets attached yet.</p>
                                <p className="text-xs text-black mt-1">Click "Add New Field" to dynamically inject data into this profile.</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {Object.entries(customFields).map(([key, data]) => (
                                    <div key={key} className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 hover:border-zinc-300 transition-colors flex flex-row items-center justify-between">
                                        <div className="flex flex-col gap-1">
                                            <p className="text-xs font-black uppercase tracking-widest text-black">{data.label}</p>
                                            <p className="font-bold text-black text-lg">{data.value}</p>
                                        </div>
                                        <div className="text-right shrink-0 ml-4">
                                            <p className="text-sm font-bold text-black font-mono uppercase tracking-widest border-2 border-black px-4 py-2 rounded-full">
                                                Added: {new Date(data.addedAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* 3. Sub-Collections Placeholder */}
                {activeTab === 'financials' && (
                    <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
                        
                        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-100">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
                                <Cloud size={24} className="text-blue-500" />
                            </div>
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight text-black flex items-center gap-2">
                                    Cloud Drive Vault
                                </h3>
                                <p className="text-xs text-black mt-1 max-w-xl">
                                    Files uploaded here are directly mapped and synced to the secure DIS Google Drive. This isolated sub-collection vault keeps your main registry index lightning fast while safely segregating all heavy assets.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-6">
                            
                            {/* Legal & Contracts */}
                            <div className="border border-zinc-200 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-black flex items-center gap-2">
                                        <FileText size={14} className="text-red-500" /> Contracts & Legal
                                    </h4>
                                    <button className="text-[9px] font-black uppercase tracking-widest text-[#8B1A2D] hover:underline bg-red-50 px-3 py-1.5 rounded-lg transition-colors hover:bg-red-100">Upload PDF</button>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer">
                                        <div className="flex items-center gap-3">
                                            <FileText size={16} className="text-black" />
                                            <div>
                                                <p className="text-xs font-bold text-black">NDA_Signed_2026.pdf</p>
                                                <p className="text-[9px] text-black uppercase tracking-widest mt-0.5">Added Aug 20, 2026 • 2.4 MB</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Media Assets */}
                            <div className="border border-zinc-200 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-black flex items-center gap-2">
                                        <Image size={14} className="text-purple-500" /> Media Assets
                                    </h4>
                                    <button className="text-[9px] font-black uppercase tracking-widest text-[#8B1A2D] hover:underline bg-red-50 px-3 py-1.5 rounded-lg transition-colors hover:bg-red-100">Upload Image/Video</button>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer">
                                        <div className="flex items-center gap-3">
                                            <Image size={16} className="text-black" />
                                            <div>
                                                <p className="text-xs font-bold text-black">Profile_Headshot_HQ.jpg</p>
                                                <p className="text-[9px] text-black uppercase tracking-widest mt-0.5">Added Aug 21, 2026 • 4.1 MB</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Financials & Invoices */}
                            <div className="border border-zinc-200 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-black flex items-center gap-2">
                                        <FileSpreadsheet size={14} className="text-emerald-500" /> Financials & Invoices
                                    </h4>
                                    <button className="text-[9px] font-black uppercase tracking-widest text-[#8B1A2D] hover:underline bg-red-50 px-3 py-1.5 rounded-lg transition-colors hover:bg-red-100">Upload CSV/PDF</button>
                                </div>
                                <div className="space-y-4">
                                    {payments.length === 0 && sponsorships.length === 0 && (
                                        <div className="text-center py-6 bg-zinc-50 rounded-lg border border-zinc-100 border-dashed">
                                            <p className="text-xs font-bold text-black">No financial records found in sub-collections.</p>
                                        </div>
                                    )}
                                    {payments.length > 0 && (
                                        <div className="space-y-2">
                                            <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Payments Ledger</h5>
                                            {payments.map(pay => (
                                                <div key={pay.id} className="flex justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                                                    <div>
                                                        <p className="text-xs font-bold text-black">{pay.description || 'Payment'}</p>
                                                        <p className="text-[9px] font-mono text-zinc-500 mt-1">{new Date(pay.date || Date.now()).toLocaleDateString()}</p>
                                                    </div>
                                                    <p className="text-sm font-bold text-emerald-600">${(pay.amount || 0).toLocaleString()}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {sponsorships.length > 0 && (
                                        <div className="space-y-2 mt-4">
                                            <h5 className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Active Sponsorships</h5>
                                            {sponsorships.map(spon => (
                                                <div key={spon.id} className="flex justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                                                    <div>
                                                        <p className="text-xs font-bold text-black">{spon.sponsorName || spon.id}</p>
                                                        <p className="text-[9px] font-mono text-zinc-500 mt-1">{spon.status || 'Active'}</p>
                                                    </div>
                                                    <p className="text-sm font-bold text-[#8B1A2D]">${(spon.valueUSD || 0).toLocaleString()}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Hardware Allocation */}
                            <div className="border border-zinc-200 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-black flex items-center gap-2">
                                        <HardDrive size={14} className="text-orange-500" /> General Logs
                                    </h4>
                                    <button className="text-[9px] font-black uppercase tracking-widest text-[#8B1A2D] hover:underline bg-red-50 px-3 py-1.5 rounded-lg transition-colors hover:bg-red-100">Upload Log</button>
                                </div>
                                <div className="text-center py-6 bg-zinc-50 rounded-lg border border-zinc-100 border-dashed">
                                    <p className="text-xs font-bold text-black">No general logs assigned.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. M&E Forecasting */}
                {activeTab === 'forecasting' && (
                    <div className="bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
                        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-zinc-100">
                            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center border border-purple-100">
                                <TrendingUp size={24} className="text-purple-500" />
                            </div>
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-tight text-black flex items-center gap-2">
                                    M&E Forecasting & Telemetry
                                </h3>
                                <p className="text-xs text-black mt-1 max-w-xl">
                                    Predictive modeling and historical velocity based on the VPRE Time Series Ledger.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div className="border border-zinc-200 rounded-xl p-5">
                                <h4 className="text-xs font-black uppercase tracking-widest text-black mb-4">Sentiment & Engagement Velocity</h4>
                                <div className="h-64 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart
                                            data={[
                                                { date: 'Week 1', sentiment: 0.8, velocity: 12 },
                                                { date: 'Week 2', sentiment: 0.85, velocity: 15 },
                                                { date: 'Week 3', sentiment: 0.6, velocity: 8 },
                                                { date: 'Week 4', sentiment: 0.75, velocity: 14 },
                                                { date: 'Week 5', sentiment: 0.9, velocity: 20 },
                                            ]}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <defs>
                                                <linearGradient id="colorSentiment" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#8B1A2D" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#8B1A2D" stopOpacity={0}/>
                                                </linearGradient>
                                                <linearGradient id="colorVelocity" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                            <XAxis dataKey="date" tick={{fontSize: 10}} tickLine={false} axisLine={false} />
                                            <YAxis yAxisId="left" tick={{fontSize: 10}} tickLine={false} axisLine={false} />
                                            <YAxis yAxisId="right" orientation="right" tick={{fontSize: 10}} tickLine={false} axisLine={false} />
                                            <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px' }} />
                                            <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }} />
                                            <Area yAxisId="left" type="monotone" dataKey="sentiment" stroke="#8B1A2D" fillOpacity={1} fill="url(#colorSentiment)" />
                                            <Area yAxisId="right" type="monotone" dataKey="velocity" stroke="#10b981" fillOpacity={1} fill="url(#colorVelocity)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="border border-zinc-200 rounded-xl p-5">
                                <h4 className="text-xs font-black uppercase tracking-widest text-black mb-4">Cohort Deviation Analysis</h4>
                                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-bold text-black">Calculated Cohort Deviation</p>
                                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Based on complex decay metrics</p>
                                    </div>
                                    <div className={`text-2xl font-black ${cohortDeviation > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {cohortDeviation > 0 ? '+' : ''}{cohortDeviation}%
                                    </div>
                                </div>
                            </div>

                            <div className="border border-zinc-200 rounded-xl p-5">
                                <h4 className="text-xs font-black uppercase tracking-widest text-black mb-4">Module Completion Trajectory</h4>
                                <div className="h-64 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={[
                                                { month: 'Jan', projected: 10, actual: 10 },
                                                { month: 'Feb', projected: 25, actual: 28 },
                                                { month: 'Mar', projected: 45, actual: 40 },
                                                { month: 'Apr', projected: 65, actual: null },
                                                { month: 'May', projected: 85, actual: null },
                                                { month: 'Jun', projected: 100, actual: null },
                                            ]}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                            <XAxis dataKey="month" tick={{fontSize: 10}} tickLine={false} axisLine={false} />
                                            <YAxis tick={{fontSize: 10}} tickLine={false} axisLine={false} />
                                            <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e4e4e7', fontSize: '12px' }} />
                                            <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold' }} />
                                            <Line type="monotone" dataKey="projected" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                                            <Line type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={3} activeDot={{ r: 8 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UniversalBible;
